import { useCallback, useRef } from 'react';
import { Editor, type RangeRef } from 'slate';

import type { SavedDocumentAnchor, SavedDocumentAnchorUpdate } from 'src/data/savedDocument';

import {
  countQuoteOccurrences,
  type DocumentText,
  getAnchorFromRange,
  getResolvedAnchorOfRange,
  isSameAnchor,
  resolveAnchor,
  type ResolvedAnchor,
} from '../lib/anchors';
import type { TrackedItem } from '../lib/review';

interface Params {
  editor: Editor;
  // Only an editable document changes under its anchors; a read-only one resolves them as stored.
  enabled: boolean;
}

const getKey = ({ type, id }: Pick<TrackedItem, 'type' | 'id'>) => `${type}:${id}`;

/**
 * Keeps a Slate range for every comment and rating while a full user edits. Slate moves the ranges
 * with each edit, so the anchors sent with the next save describe the text exactly, and the text
 * search is needed only for an anchor this editor has not tracked yet.
 */
export const useAnchorTracking = ({ editor, enabled }: Params) => {
  const rangeRefs = useRef(new Map<string, RangeRef>());
  // The last anchor each item had in this session. After its text is deleted and saved, the stored
  // anchor is null, and this is what an undo is matched against.
  const lastAnchors = useRef(new Map<string, SavedDocumentAnchor>());
  // The last text that still held each item. A revert can delete an item and write the same words
  // back in one edit. The copies are counted in this earlier text, not in the edited one.
  const lastDocuments = useRef(new Map<string, DocumentText>());
  // The copies of the quote in the text, the item's own included, before the editor saw it deleted.
  // An identical copy that was already there is not the deleted text. Only an undo brings the count
  // back, or a single edit that writes the same words over it.
  const quoteCountsBeforeDeletion = useRef(new Map<string, number>());

  const track = useCallback(
    (key: string, document: DocumentText, resolved: ResolvedAnchor) => {
      rangeRefs.current.get(key)?.unref();
      rangeRefs.current.set(key, Editor.rangeRef(editor, resolved.range, { affinity: 'inward' }));
      lastDocuments.current.set(key, document);
      quoteCountsBeforeDeletion.current.delete(key);
    },
    [editor]
  );

  const noteDeletion = useCallback(
    (key: string, document: DocumentText, anchor: SavedDocumentAnchor | undefined) => {
      if (anchor && !quoteCountsBeforeDeletion.current.has(key)) {
        const before = lastDocuments.current.get(key) ?? document;
        quoteCountsBeforeDeletion.current.set(key, countQuoteOccurrences(before, anchor.quote));
      }
    },
    []
  );

  const isRestored = useCallback(
    (key: string, document: DocumentText, anchor: SavedDocumentAnchor) => {
      noteDeletion(key, document, anchor);
      return (
        countQuoteOccurrences(document, anchor.quote) >=
        (quoteCountsBeforeDeletion.current.get(key) ?? 0)
      );
    },
    [noteDeletion]
  );

  const resolveItem = useCallback(
    (document: DocumentText, item: TrackedItem): ResolvedAnchor | null => {
      if (!item.anchor && !enabled) {
        return null;
      }

      const key = getKey(item);
      const rangeRef = enabled ? rangeRefs.current.get(key) : undefined;
      const tracked = rangeRef?.current
        ? getResolvedAnchorOfRange(editor, document, rangeRef.current)
        : null;
      if (tracked) {
        lastDocuments.current.set(key, document);
        return tracked;
      }

      // Not tracked yet, or its text was deleted in this editor. A deletion is certain, so only the
      // same words can bring it back, as an undo does.
      const anchor = item.anchor ?? lastAnchors.current.get(key);
      if (!anchor || (rangeRef && !isRestored(key, document, anchor))) {
        return null;
      }
      const resolved = resolveAnchor(editor, document, anchor, { exactOnly: Boolean(rangeRef) });
      if (resolved && enabled) {
        track(key, document, resolved);
        lastAnchors.current.set(key, anchor);
      }
      return resolved;
    },
    [editor, enabled, isRestored, track]
  );

  /** The anchors that moved since they were stored, for the save request. */
  const collectAnchorUpdates = useCallback(
    (document: DocumentText, items: TrackedItem[]): SavedDocumentAnchorUpdate[] =>
      items.flatMap((item) => {
        const key = getKey(item);
        const range = rangeRefs.current.get(key)?.current;
        if (range === undefined) {
          return [];
        }
        const tracked = range ? getResolvedAnchorOfRange(editor, document, range) : null;
        const anchor = tracked ? getAnchorFromRange(editor, document, tracked.range) : null;
        if (anchor) {
          lastAnchors.current.set(key, anchor);
          lastDocuments.current.set(key, document);
        } else {
          noteDeletion(key, document, lastAnchors.current.get(key) ?? item.anchor ?? undefined);
        }
        return isSameAnchor(anchor, item.anchor) ? [] : [{ type: item.type, id: item.id, anchor }];
      }),
    [editor, noteDeletion]
  );

  const releaseMissing = useCallback((items: TrackedItem[]) => {
    const keys = new Set(items.map(getKey));
    rangeRefs.current.forEach((rangeRef, key) => {
      if (!keys.has(key)) {
        rangeRef.unref();
        rangeRefs.current.delete(key);
        lastAnchors.current.delete(key);
        lastDocuments.current.delete(key);
        quoteCountsBeforeDeletion.current.delete(key);
      }
    });
  }, []);

  return { resolveItem, collectAnchorUpdates, releaseMissing };
};
